import NewsIcon from '@/assets/images/default-news.png';
import { type News } from '@/domain/news/types';
import { type OnUpdateProduct } from '@/features/products/components/ProductEditor';
import { toDateString } from '@/utils/datetime';
import { Typography } from '@andritzot/metris-web-ui/data-display/typography';
import { ConfirmDialog } from '@andritzot/metris-web-ui/feedback/confirm-dialog';
import { IconButton } from '@andritzot/metris-web-ui/inputs/icon-button';
import { Box } from '@andritzot/metris-web-ui/layout/content/Box';
import { DataGrid, GridColDef } from '@andritzot/metris-web-ui/mui-x/data-grid';
import DeleteOutlined from '@mui/icons-material/DeleteOutlined';
import Image from 'next/image';
import { type MouseEvent, useCallback, useMemo, useState } from 'react';

interface ProductNewsTableProps {
  news: News[];
  onEdit?: OnUpdateProduct;
  readonly?: boolean;
}

const sortNews = (a: News, b: News): 1 | -1 => (a.idNews < b.idNews ? 1 : -1);

const getRowHeight = () => 'auto' as const;

const ProductNewsTable = ({ news: newsList, onEdit, readonly = true }: ProductNewsTableProps) => {
  const newsListSorted = [...newsList].sort(sortNews);
  const [isConfirmDialogOpen, setIsConfirmDialogOpen] = useState(false);
  const [newsToDelete, setNewsToDelete] = useState<News | null>(null);

  const removeItem = (id: number) => {
    onEdit?.((value) =>
      value
        ? {
            ...value,
            news: value.news.filter((x) => x.idNews !== id),
          }
        : null,
    );
  };

  const removeNews = useCallback(
    (e: MouseEvent<HTMLButtonElement>, id: number) => {
      setNewsToDelete(newsList.find((x) => x.idNews === id) || null);
      setIsConfirmDialogOpen(true);
    },
    [newsList],
  );

  const imageTemplate = useCallback(
    (news: News) => (
      <Box
        component="a"
        href={news.contentUrl}
        target="_blank"
        rel="noreferrer"
        sx={{ display: 'block', width: '100%', height: '100%', overflow: 'hidden' }}
      >
        {news.imageUrl ? (
          <img src={news.imageUrl} alt={news.title} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
        ) : (
          <Image
            src={NewsIcon}
            alt={news.title}
            width={183}
            height={123}
            style={{ width: '100%', height: '100%', objectFit: 'cover' }}
          />
        )}
      </Box>
    ),
    [],
  );

  const titleTemplate = useCallback(
    (news: News) => (
      <Box
        component="a"
        href={news.contentUrl}
        target="_blank"
        rel="noreferrer"
        sx={{
          textDecoration: 'none',
          color: 'primary.main',
          fontWeight: 500,
          display: '-webkit-box',
          WebkitLineClamp: 2,
          WebkitBoxOrient: 'vertical',
          overflow: 'hidden',
        }}
      >
        {news.title}
      </Box>
    ),
    [],
  );

  const dateTemplate = useCallback(
    (news: News) => (
      <Typography component="p" sx={{ color: 'text.secondary', margin: 0 }}>
        {news.timestamp ? toDateString(news.timestamp, '-') : '—'}
      </Typography>
    ),
    [],
  );

  const descriptionTemplate = useCallback(
    (news: News) => (
      <Typography
        component="p"
        sx={{
          color: 'text.primary',
          margin: 0,
          display: '-webkit-box',
          WebkitLineClamp: 3,
          WebkitBoxOrient: 'vertical',
          overflow: 'hidden',
        }}
      >
        {news.description}
      </Typography>
    ),
    [],
  );

  const actionBodyTemplate = useCallback(
    (news: News) => {
      if (!readonly) {
        return (
          <Box
            sx={(theme) => ({
              display: 'flex',
              flexFlow: 'row',
              justifyContent: 'center',
              alignItems: 'center',
              ml: '0.5rem',

              [theme.breakpoints.down('md')]: {
                justifyContent: 'flex-start',
              },

              'button:first-of-type': {
                mr: '0.5rem',
              },
            })}
          >
            <IconButton
              variant="outlined"
              onClick={(e) => {
                removeNews(e, news.idNews);
              }}
              color="error"
              size="small"
            >
              <DeleteOutlined />
            </IconButton>
          </Box>
        );
      }

      return null;
    },
    [readonly, removeNews],
  );

  const columns = useMemo<GridColDef<News>[]>(
    () => [
      {
        field: 'imageUrl',
        headerName: '',
        width: 183,
        sortable: false,
        filterable: false,
        renderCell: ({ row }) => imageTemplate(row),
      },
      {
        field: 'title',
        headerName: 'Title',
        flex: 1,
        minWidth: 150,
        renderCell: ({ row }) => titleTemplate(row),
      },
      {
        field: 'timestamp',
        headerName: 'Date',
        width: 130,
        renderCell: ({ row }) => dateTemplate(row),
      },
      {
        field: 'description',
        headerName: 'Description',
        flex: 2,
        minWidth: 200,
        sortable: false,
        renderCell: ({ row }) => descriptionTemplate(row),
      },
      {
        field: 'actions',
        headerName: '',
        width: 110,
        sortable: false,
        filterable: false,
        renderCell: ({ row }) => actionBodyTemplate(row),
      },
    ],
    [actionBodyTemplate, imageTemplate, titleTemplate, dateTemplate, descriptionTemplate],
  );

  const getRowId = (row: News): string | number => row.idNews ?? `${row.title}__${row.timestamp}`;

  return (
    <Box sx={(theme) => ({ [theme.breakpoints.down('lg')]: { margin: '1rem' } })}>
      <Box>
        <DataGrid
          rows={newsListSorted}
          density="compact"
          columns={columns}
          getRowHeight={getRowHeight}
          getRowId={getRowId}
          pageSizeOptions={[4, 10, 25]}
          pagination
          initialState={{ pagination: { paginationModel: { pageSize: 4 } } }}
          localeText={{ noRowsLabel: 'No items found.' }}
        />
        <ConfirmDialog
          open={isConfirmDialogOpen}
          title="Remove Item"
          message="Are you sure you want to remove this item?"
          onClose={() => setIsConfirmDialogOpen(false)}
          onConfirm={() => {
            if (newsToDelete) {
              removeItem(newsToDelete.idNews);
              setNewsToDelete(null);
            }
            setIsConfirmDialogOpen(false);
          }}
        />
      </Box>
    </Box>
  );
};

export default ProductNewsTable;
